import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, Typography
} from "@mui/material";
import api from "../api/api";

const tipos = [
  { value: "general", label: "General" },
  { value: "puesto", label: "By Position" },
  { value: "opcional", label: "Optional" }
];

const EMPTY = {
  nombre: "",
  descripcion: "",
  tipo: "general",
  idPuesto: "",
  fecha_inicio: "",
  vigencia_meses: "",
  duracion_horas: "",
};

export default function CursoForm({ open, onClose, curso, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [puestos, setPuestos] = useState([]);

  const esNuevo = !curso;

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  useEffect(() => {
    if (curso) {
      setForm({
        nombre: curso.nombre_curso,
        descripcion: curso.descripcion || "",
        tipo: curso.tipo,
        idPuesto: "",
        fecha_inicio: curso.fecha_inicio || "",
        vigencia_meses: curso.vigencia_meses ?? "",
        duracion_horas: curso.duracion_horas ?? "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [curso, open]);

  useEffect(() => {
    if (open && esNuevo && form.tipo === "puesto") {
      api.get("/puestos").then(r => setPuestos(r.data)).catch(() => setPuestos([]));
    }
  }, [open, esNuevo, form.tipo]);

  const guardar = async () => {
    try {
      const payload = {
        nombre_curso: form.nombre,
        descripcion: form.descripcion || null,
        tipo: form.tipo,
        fecha_inicio: form.fecha_inicio || null,
        vigencia_meses: form.vigencia_meses !== "" ? Number(form.vigencia_meses) : null,
        duracion_horas: form.duracion_horas !== "" ? Number(form.duracion_horas) : null,
      };

      if (curso) {
        await api.patch(`/cursos/${curso.id_curso}`, payload);
      } else {
        if (form.tipo === "puesto" && form.idPuesto) payload.id_puesto = Number(form.idPuesto);
        await api.post("/cursos", payload);
      }

      onSaved();
      onClose();
    } catch (err) {
      alert("Error saving the course.");
      console.error(err);
    }
  };

  const vigenciaLabel = form.fecha_inicio && form.vigencia_meses
    ? `Valid from ${form.fecha_inicio} for ${form.vigencia_meses} months`
    : form.vigencia_meses
    ? "Start date is needed to calculate the expiration"
    : "No limit — valid until new version";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{curso ? "Edit Course" : "New Course"}</DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>

        <TextField
          label="Name"
          fullWidth
          value={form.nombre}
          onChange={set("nombre")}
        />

        <TextField
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={form.descripcion}
          onChange={set("descripcion")}
        />

        <TextField
          select
          label="Type"
          fullWidth
          value={form.tipo}
          onChange={(e) => setForm(prev => ({ ...prev, tipo: e.target.value, idPuesto: "" }))}
        >
          {tipos.map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
        </TextField>

        {esNuevo && form.tipo === "puesto" && (
          <TextField
            select
            label="Assign to position"
            fullWidth
            value={form.idPuesto}
            onChange={set("idPuesto")}
            helperText="Optional — you can assign it later from Assignments"
          >
            <MenuItem value="">— Unassigned —</MenuItem>
            {puestos.map((p) => (
              <MenuItem key={p.id_puesto} value={p.id_puesto}>{p.nombre_puesto}</MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          label="Start date"
          type="date"
          fullWidth
          value={form.fecha_inicio}
          onChange={set("fecha_inicio")}
          InputLabelProps={{ shrink: true }}
          helperText="Date the course begins"
        />

        <TextField
          label="Validity (months)"
          type="number"
          fullWidth
          value={form.vigencia_meses}
          onChange={set("vigencia_meses")}
          inputProps={{ min: 1, max: 120 }}
          helperText="Leave blank = valid until new version"
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          {vigenciaLabel}
        </Typography>

        <TextField
          label="Course duration (hours)"
          type="number"
          fullWidth
          value={form.duracion_horas}
          onChange={set("duracion_horas")}
          inputProps={{ min: 0.5, max: 999, step: 0.5 }}
          helperText="Will be used as the default value when logging training sessions"
        />

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={guardar}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
